



























































































































































import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';

import { Portal } from 'portal-vue';
import { VBTooltip, BButton } from 'bootstrap-vue';
import ZenMultiselect from '@reciprocity/multiselect';
import { cloneDeep, debounce } from 'lodash';

import Heatmap from './components/Heatmap.vue';
import HeatmapSlider from './components/HeatmapSlider.vue';
import HeatmapFilters from './components/HeatmapFilters.vue';
import HeatmapListing, { HeatmapRiskParams } from './components/HeatmapListing.vue';
import { DEFAULT_FILTERS, OPTION_ALL } from '@/state/modules/heatmap/helpers';

import { Threshold, HeatmapCell } from './components/heatmap_types';
import { keysToCamel, matchIds } from '@/utils';
import PrintableMultiselect from './components/PrintableMultiselect.vue';
import { getRequest } from '../../utils/client/client';

export default Vue.extend({
  name: 'RiskHeatmapPage',
  components: {
    Heatmap,
    ZenMultiselect,
    HeatmapSlider,
    HeatmapFilters,
    HeatmapListing,
    PrintableMultiselect,
    BButton,
    Portal,
  },
  directives: {
    BTooltip: VBTooltip,
  },
  data() {
    return {
      selectedCells: <HeatmapCell[]>[],
      setHeatmapSelected: <HeatmapCell[]>[],
      heatmapIsLoading: true,
      doLoadHeatmap: false,
      heatmapThresholds: <Threshold[]>[],
      riskScoreList: <Object[]>[],
      riskVectorList: <Object[]>[],
      riskVectorListLoading: true,
      loadedComponents: {},
      savedUserState: {},
      everythingLoaded: false,
      flipHeatmap: false,
    };
  },
  computed: {
    ...mapGetters(['routes', 'featureFlags']),
    ...mapGetters('heatmap', [
      'loading',
      'selectedFilters',
      'notDefaultSelectedFilters',
      'selectedScore',
      'selectedVectorX',
      'selectedVectorY',
    ]),
    xVectorOptions() {
      // move to vuex
      const copy = cloneDeep(this.riskVectorList);
      if (this.selectedVectorY.id) {
        copy.find(vector => vector.id === this.selectedVectorY.id)['$isDisabled'] = true;
      }
      return copy;
    },
    yVectorOptions() {
      const copy = cloneDeep(this.riskVectorList);
      if (this.selectedVectorX.id) {
        copy.find(vector => vector.id === this.selectedVectorX.id)['$isDisabled'] = true;
      }
      return copy;
    },
    selectedHeatmapParams(): HeatmapRiskParams {
      // move to vuex
      return {
        scoreId: (this.selectedScore || {}).id,
        xVectorId: this.selectedVectorX.id,
        yVectorId: this.selectedVectorY.id,

        selectedCells: this.selectedCells,

        objectType: this.selectedFilters.objectType,
        objectId: this.selectedFilters.objectId,
        statuses: this.parseMultipleOptions(this.selectedFilters.statuses),
        tags: this.parseMultipleOptions(this.selectedFilters.tags),
        owners: this.parseMultipleOptions(this.selectedFilters.owners),
      };
    },
  },
  watch: {
    loading: {
      handler(loading) {
        const filtersLoadedFirstTime = !loading.filters && !this.loadedComponents.filters;

        const filterObjectIdLoadedFirstTime =
          this.loadedComponents.filters && !loading.filterObjects && !this.loadedComponents.filterObjects;

        if (filtersLoadedFirstTime) {
          this.loadedComponents.filters = true;
          this.restoreFilters(this.savedUserState);
        } else if (filterObjectIdLoadedFirstTime) {
          this.componentLoaded('filtersObjects');
        }
      },
      deep: true,
      immediate: false,
    },
    selectedScore(_, oldVal) {
      // todo: move to vuex
      if (oldVal === null) {
        return;
      }
      this.setVectorX(this.findVector(this.selectedScore.heatmap_x));
      this.setVectorY(this.findVector(this.selectedScore.heatmap_y));
      this.debounceSaveUserState();
      this.fetchHeatmapThresholds();
    },
    selectedFilters: {
      handler: function() {
        this.deselectCells();
        this.debounceSaveUserState();
      },
      deep: true,
    },
    xVector() {
      this.deselectCells();
      this.debounceSaveUserState();
    },
    yVector() {
      this.deselectCells();
      this.debounceSaveUserState();
    },
  },
  mounted() {
    this.fetchFilters();
    try {
      this.savedUserState = JSON.parse(localStorage.getItem('riskHeatmapUserState')) || {};
    } catch (err) {
      this.savedUserState = {};
    }
  },
  async created() {
    this.setPageTitle({ title: 'Risk Heatmap' });
    await this.fetchRiskScores();
    await this.fetchRiskVectors();
    await this.fetchHeatmapThresholds();
  },
  methods: {
    ...mapActions(['setPageTitle', 'showAlert']),
    ...mapActions('heatmap', ['fetchFilters', 'resetFilters', 'setFilters', 'setScore', 'setVectorX', 'setVectorY']),
    findVector(vectorId) {
      return matchIds(vectorId, this.riskVectorList) || this.riskVectorList[0] || {};
    },
    heatmapItemSelected(items: array) {
      this.selectedCells = items;
      this.debounceSaveUserState();
    },
    heatmapLoadingChanged(loading: boolean) {
      this.heatmapIsLoading = loading;
    },
    parseMultipleOptions(options): Function {
      return (options || []).length ? options.join(',') : null;
    },
    async fetchHeatmapThresholds() {
      try {
        const piecesData = await getRequest({
          url: this.routes.riskHeatmapThresholds.replace('__SCORE_ID__', this.selectedScore.id),
        });
        this.heatmapThresholds = keysToCamel(piecesData.thresholds);
      } catch (error) {
        throw error;
      }
    },
    async fetchRiskScores() {
      try {
        this.riskScoreList = await getRequest({ url: this.routes.riskScores });
        this.setScore(matchIds(this.savedUserState.scoreId, this.riskScoreList));

        if (!this.selectedScore) {
          this.setScore(this.riskScoreList.length ? this.riskScoreList[0] : { id: null });
        }
      } catch (error) {
        this.showAlert({
          type: 'error',
          message: 'Oops, something went wrong.',
        });
      }
    },
    async fetchRiskVectors() {
      try {
        this.riskVectorList = await getRequest({ url: this.routes.riskVectors });

        this.setVectorX(matchIds(this.savedUserState.xVectorId, this.riskVectorList));
        this.setVectorY(matchIds(this.savedUserState.yVectorId, this.riskVectorList));

        if (this.riskVectorList.length > 1) {
          this.setVectorX(this.xVector || this.riskVectorList[1]);
          this.setVectorY(this.yVector || this.riskVectorList[0]);
        }
        this.riskVectorListLoading = false;
      } catch (error) {
        this.showAlert({
          type: 'error',
          message: 'Oops, something went wrong.',
        });
      }
    },
    componentLoaded(componentName: string) {
      /*
        NOTE: Restoring state could have been done synchronously, because we have
        the data for all queries eventhough dropdowns aren't populated. All that data was stored in the local storage.
        But it's safer to wait and just ignore things that might have been deleted (scores, vectors, tags, mapped objects,...)
      */
      if (this.everythingLoaded || this.loadedComponents[componentName]) return;
      this.loadedComponents[componentName] = true;

      switch (componentName) {
        case 'filtersObjects':
          this.setFilters({ objectId: this.savedUserState.objectId || DEFAULT_FILTERS.objectId });
          // Heatmap should be loaded only after all params are correctly set:
          this.doLoadHeatmap = true;
          break;

        case 'heatmap':
          this.setHeatmapSelected = this.savedUserState.selectedCells || [];
          this.everythingLoaded = true;
          break;
      }
    },
    restoreFilters(state: object) {
      // move to vuex

      const getOptions = state => {
        if (state === null || state === undefined) {
          return [];
        }
        return (state || '')
          .split(',')
          .filter(o => o !== undefined) // del items that doesn't exist anymore
          .map(o => o || OPTION_ALL.value);
      };

      this.setFilters({
        tags: getOptions(state['tags']),
        statuses: getOptions(state['statuses']),
        owners: getOptions(state['owners']),
        objectType: state.objectType || DEFAULT_FILTERS.objectType,
      });

      // If no exact filter object is specified, we're done - Load heatmap:
      // selectedFilters.objectType might have been removed by store if it had invalid value:
      const validObjectTypeSelected = !!this.selectedFilters.objectType;
      if (!state.objectId || !validObjectTypeSelected) {
        this.componentLoaded('filtersObjects');
      }
    },
    debounceSaveUserState: debounce(function() {
      // move to vuex

      if (!this.everythingLoaded) return; // todo: move to vuex when everything else is moved

      let params = cloneDeep(this.selectedHeatmapParams);
      params.selectedCells = this.selectedCells.map(o => o.cellIndex);
      params.flipHeatmap = this.flipHeatmap;
      localStorage.setItem('riskHeatmapUserState', JSON.stringify(params));
    }, 1000),
    deselectCells() {
      this.selectedCells = [];
      this.setHeatmapSelected = [];
    },
    print() {
      window.print();
    },
    selectionToggle() {
      this.$emit('selectionToggle');
    },
    toggleFlipHeatmap() {
      this.flipHeatmap = !this.flipHeatmap;
      this.debounceSaveUserState();
    },
    selectCellsBack() {
      this.setHeatmapSelected = [...this.selectedCells.map(o => o.cellIndex)];
    },
  },
});
